import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  FormHelperText,
  Autocomplete,
  Chip,
  Tooltip,
  Card,
  CardContent,
  Container,
  Collapse
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Add, Delete, Search, Assignment, CheckCircle, Error, Warning, Info } from '@mui/icons-material';
import { fetchCustomerRequestByProjectCode, fetchBusinessGroups, fetchDepartments, fetchBudgetCodes, fetchCustomerByBudgetCode, fetchTypeOfWork, submitCRF, updateCustomerRequest, fetchModelDetails } from '../../services/dataService';
import ModelSearchDialog from '../../components/ModelSearchDialog';
import { useNavigate } from 'react-router-dom';
import { CrfValidationUtils } from '../../utils/crfValidationEnhanced';
import { validateFieldCharacterLimit, validateAllCharacterLimits, getCharacterCountHelperText, getCharacterCountColor } from '../../utils/characterLimitValidation';
import usePageTitle from '../../utils/usePageTitle';

const ModernCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  border: '1px solid #e8eaed',
  background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
    transform: 'translateY(-2px)'
  },
  '&:focus-within': {
    outline: `3px solid ${theme.palette.primary.main}40`,
    outlineOffset: '2px'
  }
}));

const SectionCard = styled(Card)(({ theme, variant = 'default' }) => {
  const variants = {
    default: { background: '#ffffff', border: '1px solid #e8eaed' },
    primary: { background: 'linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%)', border: `1px solid ${theme.palette.primary.light}40` },
    secondary: { background: 'linear-gradient(135deg, #fff8f0 0%, #ffffff 100%)', border: '1px solid #ffeaa7' }
  };
  return {
    borderRadius: 12,
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    marginBottom: theme.spacing(3),
    transition: 'all 0.2s ease-in-out',
    ...variants[variant],
    '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }
  };
});

const ModernTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: '#fafbfc',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: '#f5f6f7',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: '2px'
      }
    },
    '&.Mui-focused': {
      backgroundColor: '#ffffff',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: '2px',
        boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`
      }
    }
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
    '&.Mui-focused': { color: theme.palette.primary.main, fontWeight: 600 }
  }
}));

const ModernButton = styled(Button)(({ theme, variant = 'contained' }) => ({
  borderRadius: 12,
  textTransform: 'none',
  fontWeight: 600,
  padding: '12px 32px',
  fontSize: '0.95rem',
  minHeight: '48px',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: variant === 'contained' ? '0 4px 12px rgba(0,0,0,0.15)' : 'none',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: variant === 'contained' ? '0 6px 20px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.1)'
  },
  '&:focus': {
    outline: `3px solid ${theme.palette.primary.main}40`,
    outlineOffset: '2px'
  }
}));

const CustomerRequestForm = () => {
  const { id } = useParams();
  const projectCode = id;
  usePageTitle(projectCode ? `Edit Customer Request - ${projectCode}` : 'New Customer Request');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetchingCustomer, setFetchingCustomer] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [characterLimitErrors, setCharacterLimitErrors] = useState([]);
  const [duplicateModelErrors, setDuplicateModelErrors] = useState([]);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  
  // Refs for focusing fields
  const alertAreaRef = useRef(null);
  const requestorNameRef = useRef(null);
  const businessGroupRef = useRef(null);
  const budgetCodeRef = useRef(null);
  const picContactRef = useRef(null);
  const emailAddressRef = useRef(null);
  const workToBePerformedRef = useRef(null);
  const problemDescriptionRef = useRef(null);
  const [businessGroups, setBusinessGroups] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [budgetCodes, setBudgetCodes] = useState([]);
  const [typeOfWorkOptions, setTypeOfWorkOptions] = useState([]);
  const [modelSearchOpen, setModelSearchOpen] = useState(false);
  const [selectedModelRowId, setSelectedModelRowId] = useState(null);
  const [searchFieldType, setSearchFieldType] = useState('model'); // 'model' or 'to'

  const [formData, setFormData] = useState({
    requestorName: '',
    businessGroup: '',
    budgetCode: '',
    fullCustomerName: '',
    department: '',
    picContact: '',
    emailAddress: '',
    businessName: '',
    streetAddress: '',
    cityStateZip: '',
    customerType: 'Local',
    typeOfWork: '',
    location: 'QA',
    projectName: '',
    description: '',
    workToBePerformed: '',
    problemDescription: '',
    carsonComments: ''
  });

  const [models, setModels] = useState([
    {
      id: 1,
      model: '',
      to: '',
      quantity: '',
      dateRequired: '',
      dateOfJobLine: '',
      lineNum: '',
      personInCharge: '',
      hasWorkOrder: false,
      isModified: false
    }
  ]);

  const loadCustomerRequestData = useCallback(async () => {
    if (!projectCode) return;
    
    setLoading(true);
    try {
      const data = await fetchCustomerRequestByProjectCode(projectCode);
      setFormData(prev => ({
        ...prev,
        requestorName: data.requestorName || '',
        businessGroup: data.businessGroup || data.group_name || '',
        budgetCode: data.budgetCode || data.budget_code || '',
        fullCustomerName: data.fullCustomerName || data.cust_name || '',
        department: data.department || data.dept_name || '',
        picContact: data.picContact || data.contact || '',
        emailAddress: data.emailAddress || data.email_address || '',
        businessName: data.businessName || data.address_1 || '',
        streetAddress: data.streetAddress || data.address_2 || '',
        cityStateZip: data.cityStateZip || data.address_3 || '',
        customerType: data.customerType === 'L' ? 'Local' : 'Overseas',
        typeOfWork: data.typeOfWork || data.type_of_work || '',
        location: data.location || 'QA',
        projectName: data.projectName || data.project_name || '',
        description: data.description || data.project_description || '',
        workToBePerformed: data.workToBePerformed || data.work_to_be_performed || '',
        problemDescription: data.problemDescription || data.problem_description || '',
        carsonComments: data.carsonComments || data.carson_comments || ''
      }));
      
      // Fetch models separately to ensure we get the latest data with work order info
      try {
        const modelData = await fetchModelDetails(projectCode);
        if (modelData && modelData.length > 0) {
          setModels(modelData.map((model, index) => {
            // Ensure date is properly formatted for input fields
            let dateRequired = '';
            if (model.dateRequired) {
              // Handle both ISO string and date object formats
              if (typeof model.dateRequired === 'string') {
                dateRequired = model.dateRequired.includes('T') ? 
                  model.dateRequired.split('T')[0] : model.dateRequired;
              } else if (model.dateRequired instanceof Date) {
                dateRequired = model.dateRequired.toISOString().split('T')[0];
              }
            }
            
            let dateOfJobLine = '';
            if (model.dateOfJobLine) {
              // Handle both ISO string and date object formats
              if (typeof model.dateOfJobLine === 'string') {
                dateOfJobLine = model.dateOfJobLine.includes('T') ? 
                  model.dateOfJobLine.split('T')[0] : model.dateOfJobLine;
              } else if (model.dateOfJobLine instanceof Date) {
                dateOfJobLine = model.dateOfJobLine.toISOString().split('T')[0];
              }
            }
            
            return {
              id: index + 1,
              model_dtl_id: model.id, // Store the actual database ID
              model: model.model || model.from_model || '',
              to: model.to || model.to_model || '',
              quantity: model.quantity || '',
              dateRequired: dateRequired,
              dateOfJobLine: dateOfJobLine,
              lineNum: model.lineNum || model.line_num || '',
              personInCharge: model.personInCharge || model.pic_name || '',
              hasWorkOrder: model.hasWorkOrder || false,
              isModified: false // Loaded data is not modified initially
            };
          }));
        }
      } catch (modelError) {
        console.warn('Failed to fetch model details separately:', modelError.message);
      }
    } catch (error) {
      console.error('Error loading customer request data:', error);
    } finally {
      setLoading(false);
    }
  }, [projectCode]);

  useEffect(() => {
    loadCustomerRequestData();
    
    const loadBusinessGroups = async () => {
      try {
        const groups = await fetchBusinessGroups();
        setBusinessGroups(groups);
      } catch (error) {
        console.error('Error loading business groups:', error);
      }
    };
    
    const loadBudgetCodes = async () => {
      try {
        const codes = await fetchBudgetCodes();
        setBudgetCodes(codes);
      } catch (error) {
        console.error('Error loading budget codes:', error);
      }
    };
    
    const loadTypeOfWork = async () => {
      try {
        const types = await fetchTypeOfWork();
        setTypeOfWorkOptions(types);
      } catch (error) {
        console.error('Error loading type of work:', error);
      }
    };
    
    loadBusinessGroups();
    loadBudgetCodes();
    loadTypeOfWork();
  }, [loadCustomerRequestData]);

  useEffect(() => {
    const loadDepartments = async () => {
      if (formData.businessGroup === 'BBSC') {
        try {
          const depts = await fetchDepartments();
          setDepartments(depts);
        } catch (error) {
          console.error('Error loading departments:', error);
        }
      } else {
        setDepartments([]);
      }
    };
    loadDepartments();
  }, [formData.businessGroup]);

  const handleChange = (field) => async (event) => {
    const value = event.target.value;
    
    // Validate character limit for this field
    const validation = validateFieldCharacterLimit(field, value);
    if (!validation.isValid) {
      setCharacterLimitErrors(prev => {
        const filtered = prev.filter(error => error.field !== field);
        return [...filtered, {
          field,
          message: validation.message,
          type: 'CHARACTER_LIMIT_EXCEEDED'
        }];
      });
    } else {
      setCharacterLimitErrors(prev => prev.filter(error => error.field !== field));
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (field === 'budgetCode' && value) {
      setFetchingCustomer(true);
      try {
        const customerData = await fetchCustomerByBudgetCode(value);
        if (customerData) {
          setFormData(prev => ({
            ...prev,
            fullCustomerName: customerData.customerName || '',
            department: prev.businessGroup === 'BBSC' ? prev.department : (customerData.department || ''),
            businessName: customerData.address1 || '',
            streetAddress: customerData.address2 || '',
            cityStateZip: customerData.address3 || '',
            customerType: customerData.customerType === 'L' ? 'Local' : 'Overseas'
          }));
        }
      } catch (error) {
        console.error('Error fetching customer details:', error);
      } finally {
        setFetchingCustomer(false);
      }
    }
    
    if (field === 'typeOfWork') {
      const showToColumn = value === 'CFG' || value === 'DFG';
      if (!showToColumn) {
        setModels(prev => prev.map(model => ({ ...model, to: '' })));
      }
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(false);
    setHasAttemptedSubmit(true); // Mark that user has attempted to submit
    
    // Validate character limits
    const characterErrors = validateAllCharacterLimits(formData, models);
    setCharacterLimitErrors(characterErrors);
    
    // Use enhanced validation
    const validationErrors = CrfValidationUtils.validateCrfForSubmission(formData, models);
    const businessRuleErrors = CrfValidationUtils.validateBusinessRules(formData, models);
    const allErrors = [...validationErrors, ...businessRuleErrors, ...characterErrors, ...duplicateModelErrors];
    
    if (allErrors.length > 0) {
      setSubmitError(CrfValidationUtils.formatValidationErrors(allErrors));
      
      // Focus alert area first
      setTimeout(() => {
        if (alertAreaRef.current) {
          alertAreaRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center'
          });
          alertAreaRef.current.focus();
        }
      }, 100);
      
      return;
    }
    
    setLoading(true);
    
    try {
      const storedUser = JSON.parse(sessionStorage.getItem('userInfo') || '{}');
      const submitData = {
        ...formData,
        models: models.filter(m => m.model && m.model.trim()).map(model => ({
          ...model,
          // Only include model_dtl_id for existing records (loaded from DB)
          // New rows won't have model_dtl_id, so don't fall back to the sequential id
          model_dtl_id: model.model_dtl_id || null
        })),
        projectCode: id || null,
        requestor_id: 'SYSADMIN',
        requestor_email: 'SYSADMIN',
        user: {
          username: storedUser?.username || 'SYSADMIN',
          id: storedUser?.userId || storedUser?.id || 'SYSADMIN'
        }
      };
      
      let result;
      if (projectCode) {
        // Update existing customer request
        result = await updateCustomerRequest(projectCode, submitData);
      } else {
        // Create new customer request using submitCRF
        result = await submitCRF(submitData);
      }
      
      if (result.validationCode && result.validationCode !== CrfValidationUtils.CRF_VALIDATION_CODES.SUCCESS) {
        throw new Error(result.validationMessage || 'Validation failed');
      }
      
      setSubmitSuccess(true);
      setTimeout(() => {
        navigate('/customer-request');
      }, 2000);
    } catch (error) {
      console.error('Error submitting customer request:', error);
      
      // Handle specific CRF validation errors
      let errorMessage = error.response?.data?.error || error.message || 'Failed to submit customer request';
      
      if (errorMessage.includes('DUPLICATE_RECORD_FOUND')) {
        errorMessage = 'Duplicate model entry detected. Please check your model rows for duplicate Model/To Model combinations and remove or update them before resubmitting.';
      } else if (errorMessage.includes('ID conflict')) {
        errorMessage = 'A temporary conflict occurred. Please try submitting again.';
      } else if (errorMessage.includes('Duplicate entry')) {
        errorMessage = 'A record conflict occurred. Please try submitting again.';
      } else if (errorMessage.includes('PROJECT_ALREADY_TERMINATED')) {
        errorMessage = 'This project has already been terminated and cannot be modified.';
      } else if (errorMessage.includes('PROJECT_ALREADY_CLOSED')) {
        errorMessage = 'This project has already been closed and cannot be modified.';
      } else if (errorMessage.includes('MODIFIED_BY_OTHER_USER')) {
        errorMessage = 'This record has been modified by another user. Please refresh and try again.';
      }
      
      setSubmitError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleModelChange = (id, field, value) => {
    // Validate character limits for model fields
    if (field === 'model' || field === 'personInCharge') {
      const fieldName = field === 'model' ? 'modelCode' : field;
      const validation = validateFieldCharacterLimit(fieldName, value);
      const errorKey = `${field}_${id}`;
      
      if (!validation.isValid) {
        setCharacterLimitErrors(prev => {
          const filtered = prev.filter(error => error.field !== errorKey);
          return [...filtered, {
            field: errorKey,
            message: `Model Row ${id}: ${validation.message}`,
            type: 'CHARACTER_LIMIT_EXCEEDED'
          }];
        });
      } else {
        setCharacterLimitErrors(prev => prev.filter(error => error.field !== errorKey));
      }
    }
    
    // Update the model data with modification tracking
    const updatedModels = models.map(model => 
      model.id === id ? { 
        ...model, 
        [field]: value,
        isModified: true // Track that this model has been modified
      } : model
    );
    setModels(updatedModels);
    
    // Validate for duplicates in real-time if model or to field changed
    if (field === 'model' || field === 'to') {
      const duplicateErrors = CrfValidationUtils.validateDuplicateModels(updatedModels);
      // Only show duplicate errors if user has attempted to submit
      setDuplicateModelErrors(hasAttemptedSubmit ? duplicateErrors : []);
    }
  };

  const addModelRow = () => {
    const newId = Math.max(...models.map(m => m.id)) + 1;
    const newModels = [...models, {
      id: newId,
      model: '',
      to: '',
      quantity: '',
      dateRequired: '',
      dateOfJobLine: '',
      lineNum: '',
      personInCharge: '',
      hasWorkOrder: false,
      isModified: true // New rows are considered modified
    }];
    setModels(newModels);
    
    // Clear duplicate errors when adding new row
    // Only clear if user hasn't attempted to submit yet
    if (!hasAttemptedSubmit) {
      setDuplicateModelErrors([]);
    }
  };

  const handleModelSearch = (rowId, fieldType) => {
    setSelectedModelRowId(rowId);
    setSearchFieldType(fieldType);
    setModelSearchOpen(true);
  };

  const handleModelSelect = (modelCode) => {
    if (selectedModelRowId && searchFieldType) {
      handleModelChange(selectedModelRowId, searchFieldType, modelCode);
    }
    setModelSearchOpen(false);
    setSelectedModelRowId(null);
    setSearchFieldType('model');
  };

  const handleModelSearchClose = () => {
    setModelSearchOpen(false);
    setSelectedModelRowId(null);
    setSearchFieldType('model');
  };

  const deleteModelRow = (id) => {
    if (models.length > 1) {
      const updatedModels = models.filter(model => model.id !== id);
      setModels(updatedModels);
      
      // Re-validate duplicates after deletion
      const duplicateErrors = CrfValidationUtils.validateDuplicateModels(updatedModels);
      // Only show duplicate errors if user has attempted to submit
      setDuplicateModelErrors(hasAttemptedSubmit ? duplicateErrors : []);
    }
  };



  // Helper function to check if a model field has duplicate error
  const hasModelDuplicateError = (modelId, fieldType) => {
    // Only show duplicate errors if user has attempted to submit
    if (!hasAttemptedSubmit) return false;
    return duplicateModelErrors.some(error => {
      const fieldPattern = `models[${models.findIndex(m => m.id === modelId)}].${fieldType}`;
      return error.field === fieldPattern;
    });
  };

  // Helper function to get duplicate error message for a model field
  const getModelDuplicateErrorMessage = (modelId, fieldType) => {
    // Only show duplicate errors if user has attempted to submit
    if (!hasAttemptedSubmit) return '';
    const error = duplicateModelErrors.find(error => {
      const fieldPattern = `models[${models.findIndex(m => m.id === modelId)}].${fieldType}`;
      return error.field === fieldPattern;
    });
    return error ? error.message : '';
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }} role="status" aria-live="polite">
        <CircularProgress aria-label="Loading customer request form" />
        <Typography sx={{ position: 'absolute', left: '-10000px' }}>Loading customer request form</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Enhanced Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Typography 
              variant="h3" 
              component="h1" 
              sx={{ 
                fontWeight: 700, 
                color: '#1a1a1a', 
                mb: 1,
                background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              {projectCode ? 'Edit Customer Request' : 'New Customer Request'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {projectCode ? 'Update your existing request details' : 'Create a new customer service request'}
            </Typography>
            {projectCode && (
              <Chip 
                label={`Project: ${projectCode}`}
                color="primary" 
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: '0.9rem' }}
                icon={<Assignment aria-hidden="true" />}
              />
            )}
          </Box>
          <Chip 
            label={projectCode ? 'Edit Mode' : 'Create Mode'}
            color={projectCode ? 'warning' : 'success'}
            variant="filled"
            size="medium"
            sx={{ fontWeight: 600 }}
          />
        </Box>
      </Box>

      {/* Enhanced Alerts */}
      <Box ref={alertAreaRef} tabIndex={-1} sx={{ outline: 'none', mb: 3 }}>
        <Collapse in={!!submitError}>
          <Alert 
            severity="error" 
            sx={{ mb: 2, borderRadius: 2 }}
            icon={<Error aria-hidden="true" />}
            role="alert"
            aria-live="assertive"
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Error</Typography>
            {submitError}
          </Alert>
        </Collapse>
        
        <Collapse in={duplicateModelErrors.length > 0 && hasAttemptedSubmit}>
          <Alert 
            severity="error" 
            sx={{ mb: 2, borderRadius: 2 }}
            icon={<Error aria-hidden="true" />}
            role="alert"
            aria-live="assertive"
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Duplicate Model Errors:</Typography>
            {duplicateModelErrors.map((error, index) => (
              <Typography key={index} variant="body2" sx={{ ml: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'error.main' }} aria-hidden="true" />
                {error.message}
              </Typography>
            ))}
          </Alert>
        </Collapse>
        
        <Collapse in={characterLimitErrors.length > 0}>
          <Alert 
            severity="warning" 
            sx={{ mb: 2, borderRadius: 2 }}
            icon={<Warning aria-hidden="true" />}
            role="alert"
            aria-live="polite"
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Character Limit Warnings:</Typography>
            {characterLimitErrors.map((error, index) => (
              <Typography key={index} variant="body2" sx={{ ml: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'warning.main' }} aria-hidden="true" />
                {error.message}
              </Typography>
            ))}
          </Alert>
        </Collapse>
        
        <Collapse in={submitSuccess}>
          <Alert 
            severity="success" 
            sx={{ mb: 2, borderRadius: 2 }}
            icon={<CheckCircle aria-hidden="true" />}
            role="alert"
            aria-live="polite"
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Customer request {projectCode ? 'updated' : 'submitted'} successfully! Redirecting...
            </Typography>
          </Alert>
        </Collapse>
      </Box>

      {/* Main Form */}
      <ModernCard sx={{ width: '100%', maxWidth: '100%' }}>
        <CardContent sx={{ p: 4, width: '100%' }}>
          <form onSubmit={handleSubmit} aria-label="Customer request submission form">
            {/* Single Form Section */}
            <SectionCard variant="primary">
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ '& > .MuiBox-root': { mb: 3 } }}>
                  {/* Row 1: Location - Full Width */}
                  <Box sx={{ width: '100%', mb: 3 }}>
                    <FormControl fullWidth required>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                        Location *
                      </Typography>
                      <RadioGroup
                        row
                        value={formData.location}
                        onChange={handleChange('location')}
                        aria-label="Location selection"
                        sx={{ gap: 2 }}
                      >
                        <FormControlLabel 
                          value="QA" 
                          control={<Radio disabled={!!projectCode} inputProps={{ 'aria-disabled': !!projectCode ? 'true' : undefined }} />} 
                          label="QA"
                        />
                        <FormControlLabel 
                          value="PSA" 
                          control={<Radio disabled={!!projectCode} inputProps={{ 'aria-disabled': !!projectCode ? 'true' : undefined }} />} 
                          label="PSA"
                        />
                      </RadioGroup>
                      {projectCode && (
                        <FormHelperText sx={{ mt: 1 }}>
                          Location cannot be changed in edit mode
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Box>

                  {/* Row 2: Requestor Name & Business Group */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <Box sx={{ flex: 1 }}>
                      <ModernTextField
                        fullWidth
                        required
                        label="Requestor Name"
                        value={formData.requestorName}
                        onChange={handleChange('requestorName')}
                        inputProps={{ 
                          maxLength: 50,
                          'aria-describedby': 'requestor-name-helper'
                        }}
                        inputRef={requestorNameRef}
                        helperText={getCharacterCountHelperText('requestorName', formData.requestorName)}
                        error={!validateFieldCharacterLimit('requestorName', formData.requestorName).isValid}
                        FormHelperTextProps={{ 
                          id: 'requestor-name-helper',
                          sx: { color: getCharacterCountColor('requestorName', formData.requestorName) }
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <FormControl fullWidth required>
                        <InputLabel>Business Group</InputLabel>
                        <Select
                          value={formData.businessGroup}
                          onChange={handleChange('businessGroup')}
                          label="Business Group"
                          ref={businessGroupRef}
                          sx={{ borderRadius: 2 }}
                        >
                          {businessGroups.map((group) => (
                            <MenuItem key={group.code || group} value={group.code || group}>
                              <Box>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                  {group.code || group}
                                </Typography>
                                {group.name && group.name !== group.code && (
                                  <Typography variant="body2" color="text.secondary">
                                    {group.name}
                                  </Typography>
                                )}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>

                  {/* Row 3: Budget Code & Full Customer Name */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <Box sx={{ flex: 1 }}>
                      <Autocomplete
                        fullWidth
                        options={budgetCodes}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') return option;
                          return `${option.code || option} - ${option.description || option}`;
                        }}
                        value={budgetCodes.find(code => (code.code || code) === formData.budgetCode) || formData.budgetCode}
                        onChange={async (event, newValue) => {
                          const value = newValue ? (newValue.code || newValue) : '';
                          
                          setFormData(prev => ({ ...prev, budgetCode: value }));
                          
                          if (value) {
                            setFetchingCustomer(true);
                            try {
                              const customerData = await fetchCustomerByBudgetCode(value);
                              if (customerData) {
                                setFormData(prev => ({
                                  ...prev,
                                  budgetCode: value,
                                  fullCustomerName: customerData.customerName || '',
                                  department: prev.businessGroup === 'BBSC' ? prev.department : (customerData.department || ''),
                                  businessName: customerData.address1 || '',
                                  streetAddress: customerData.address2 || '',
                                  cityStateZip: customerData.address3 || '',
                                  customerType: customerData.customerType === 'L' ? 'Local' : 'Overseas'
                                }));
                              }
                            } catch (error) {
                              console.error('Error fetching customer details:', error);
                            } finally {
                              setFetchingCustomer(false);
                            }
                          }
                        }}
                        disabled={fetchingCustomer}
                        renderInput={(params) => (
                          <ModernTextField
                            {...params}
                            required
                            label="Budget Code"
                            ref={budgetCodeRef}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {fetchingCustomer && <CircularProgress size={20} aria-label="Fetching customer details" />}
                                  {params.InputProps.endAdornment}
                                </>
                              )
                            }}
                            helperText="Select or type a budget code to auto-populate customer details"
                          />
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <ModernTextField
                        fullWidth
                        label="Full Customer Name"
                        value={formData.fullCustomerName}
                        onChange={handleChange('fullCustomerName')}
                        inputProps={{ maxLength: 200 }}
                        helperText="Auto-populated from budget code (editable)"
                      />
                    </Box>
                  </Box>

                  {/* Row 4: Department & Business Name */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <Box sx={{ flex: 1 }}>
                      {formData.businessGroup === 'BBSC' ? (
                        <FormControl fullWidth required>
                          <InputLabel>Department</InputLabel>
                          <Select
                            value={formData.department}
                            onChange={handleChange('department')}
                            label="Department"
                            sx={{ borderRadius: 2 }}
                          >
                            {departments.map((dept) => (
                              <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <ModernTextField
                          fullWidth
                          label="Department"
                          value={formData.department}
                          onChange={handleChange('department')}
                          inputProps={{ maxLength: 100 }}
                          helperText="Auto-populated from budget code (editable)"
                        />
                      )}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <ModernTextField
                        fullWidth
                        label="Business Name"
                        value={formData.businessName}
                        onChange={handleChange('businessName')}
                        inputProps={{ maxLength: 200 }}
                        helperText="Auto-populated from budget code (editable)"
                      />
                    </Box>
                  </Box>

                  {/* Row 5: Street Address & City State Zip Code */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <Box sx={{ flex: 1 }}>
                      <ModernTextField
                        fullWidth
                        label="Street Address"
                        value={formData.streetAddress}
                        onChange={handleChange('streetAddress')}
                        inputProps={{ maxLength: 200 }}
                        helperText="Auto-populated from budget code (editable)"
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <ModernTextField
                        fullWidth
                        label="City, State, Zip Code"
                        value={formData.cityStateZip}
                        onChange={handleChange('cityStateZip')}
                        inputProps={{ maxLength: 200 }}
                        helperText="Auto-populated from budget code (editable)"
                      />
                    </Box>
                  </Box>

                  {/* Row 6: PIC & Email Address */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <Box sx={{ flex: 1 }}>
                      <ModernTextField
                        fullWidth
                        required
                        label="PIC"
                        value={formData.picContact}
                        onChange={handleChange('picContact')}
                        inputProps={{ maxLength: 50 }}
                        inputRef={picContactRef}
                        helperText={getCharacterCountHelperText('picContact', formData.picContact)}
                        error={!validateFieldCharacterLimit('picContact', formData.picContact).isValid}
                        FormHelperTextProps={{
                          sx: { color: getCharacterCountColor('picContact', formData.picContact) }
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <ModernTextField
                        fullWidth
                        required
                        label="Email Address"
                        value={formData.emailAddress}
                        onChange={handleChange('emailAddress')}
                        type="text"
                        inputProps={{ maxLength: 500 }}
                        inputRef={emailAddressRef}
                        helperText={getCharacterCountHelperText('emailAddress', formData.emailAddress, 'Separate multiple email addresses with semicolon (;)')}
                        error={!validateFieldCharacterLimit('emailAddress', formData.emailAddress).isValid}
                        FormHelperTextProps={{
                          sx: { color: getCharacterCountColor('emailAddress', formData.emailAddress) }
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Customer Type Radio Group */}
                  <Box sx={{ width: '100%', mb: 3 }}>
                    <FormControl component="fieldset">
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                        Customer Type
                      </Typography>
                      <RadioGroup
                        row
                        value={formData.customerType}
                        onChange={handleChange('customerType')}
                        sx={{ gap: 2 }}
                      >
                        <FormControlLabel value="Local" control={<Radio />} label="Local" />
                        <FormControlLabel value="Overseas" control={<Radio />} label="Overseas" />
                      </RadioGroup>
                      <FormHelperText>Auto-populated from budget code (editable)</FormHelperText>
                    </FormControl>
                  </Box>

                  {/* Work Details Fields */}
                  <Box sx={{ '& > .MuiBox-root': { mb: 3 } }}>
                    {/* Type of Work - Full Width */}
                    <Box sx={{ width: '100%', mb: 3 }}>
                      <FormControl fullWidth required>
                        <InputLabel>Type of Work</InputLabel>
                        <Select
                          value={formData.typeOfWork}
                          onChange={handleChange('typeOfWork')}
                          label="Type of Work"
                          sx={{ borderRadius: 2, width: '100%' }}
                        >
                          {typeOfWorkOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Row 7: Work to be Performed - Full Width */}
                    <Box sx={{ width: '100%', mb: 3 }}>
                      <ModernTextField
                        fullWidth
                        required
                        label="Work to be Performed"
                        value={formData.workToBePerformed}
                        onChange={handleChange('workToBePerformed')}
                        inputProps={{ maxLength: 100 }}
                        inputRef={workToBePerformedRef}
                        helperText={getCharacterCountHelperText('workToBePerformed', formData.workToBePerformed)}
                        error={!validateFieldCharacterLimit('workToBePerformed', formData.workToBePerformed).isValid}
                        FormHelperTextProps={{
                          sx: { color: getCharacterCountColor('workToBePerformed', formData.workToBePerformed) }
                        }}
                        sx={{ width: '100%' }}
                      />
                    </Box>

                    {/* Row 8: Problem Description and Comments (Max 4000 characters) - Full Width */}
                    <Box sx={{ width: '100%', mb: 3 }}>
                      <ModernTextField
                        fullWidth
                        required
                        multiline
                        rows={4}
                        label="Problem Description and Comments"
                        value={formData.problemDescription}
                        onChange={handleChange('problemDescription')}
                        inputProps={{ maxLength: 4000 }}
                        inputRef={problemDescriptionRef}
                        helperText={getCharacterCountHelperText('problemDescription', formData.problemDescription)}
                        error={!validateFieldCharacterLimit('problemDescription', formData.problemDescription).isValid}
                        FormHelperTextProps={{
                          sx: { color: getCharacterCountColor('problemDescription', formData.problemDescription) }
                        }}
                        sx={{ width: '100%' }}
                      />
                    </Box>

                    {/* Row 9: Carson Comments (Max 4000 characters) */}
                    <Box sx={{ width: '100%', mt: 3 }}>
                      <ModernTextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Carson Comments"
                        value={formData.carsonComments}
                        onChange={handleChange('carsonComments')}
                        inputProps={{ maxLength: 4000 }}
                        helperText={getCharacterCountHelperText('carsonComments', formData.carsonComments, '(Optional)')}
                        error={!validateFieldCharacterLimit('carsonComments', formData.carsonComments).isValid}
                        FormHelperTextProps={{
                          sx: { color: getCharacterCountColor('carsonComments', formData.carsonComments) }
                        }}
                        sx={{ width: '100%' }}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Models Section */}
                <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                  <ModernButton 
                    variant="contained" 
                    startIcon={<Add aria-hidden="true" />} 
                    onClick={addModelRow}
                    size="medium"
                    aria-label="Add new model row to table"
                    sx={{ 
                      background: '#000000',
                      '&:hover': {
                        background: '#333333'
                      }
                    }}
                  >
                    Add Model Row
                  </ModernButton>
                  <Typography variant="body2" color="text.secondary">
                    {models.length} model{models.length !== 1 ? 's' : ''} configured
                  </Typography>
                </Box>

                <TableContainer 
                  component={Paper} 
                  sx={{ 
                    borderRadius: 3, 
                    overflow: 'auto', 
                    width: '100%',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    border: '1px solid #e8eaed'
                  }}
                >
                  <Table 
                    size="medium" 
                    aria-label="Models configuration table" 
                    sx={{ 
                      tableLayout: 'auto', 
                      width: '100%', 
                      minWidth: projectCode ? 1200 : 800,
                      '& .MuiTableCell-head': {
                        backgroundColor: '#f8f9ff',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        borderBottom: '2px solid #e8eaed'
                      },
                      '& .MuiTableCell-body': {
                        borderBottom: '1px solid #f0f0f0'
                      },
                      '& .MuiTableRow-root:hover': {
                        backgroundColor: '#fafbfc'
                      }
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell component="th" scope="col" sx={{ minWidth: 100 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Assignment sx={{ fontSize: 18 }} aria-hidden="true" />
                            Model *
                          </Box>
                        </TableCell>
                        {(formData.typeOfWork === 'CFG' || formData.typeOfWork === 'DFG') && (
                          <TableCell component="th" scope="col" sx={{ minWidth: 100 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Assignment sx={{ fontSize: 18 }} aria-hidden="true" />
                              To Model
                            </Box>
                          </TableCell>
                        )}
                        <TableCell component="th" scope="col" sx={{ minWidth: 80 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" aria-hidden="true">#</Typography>
                            Quantity *
                          </Box>
                        </TableCell>
                        <TableCell component="th" scope="col" sx={{ minWidth: 100 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" aria-hidden="true">📅</Typography>
                            Date Required *
                          </Box>
                        </TableCell>
                        {projectCode && (
                          <>
                            <TableCell component="th" scope="col" sx={{ minWidth: 100 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" aria-hidden="true">📅</Typography>
                                Date of Job
                              </Box>
                            </TableCell>
                            <TableCell component="th" scope="col" sx={{ minWidth: 80 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" aria-hidden="true">#</Typography>
                                Line
                              </Box>
                            </TableCell>
                            <TableCell component="th" scope="col" sx={{ minWidth: 120 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" aria-hidden="true">👤</Typography>
                                PIC
                              </Box>
                            </TableCell>
                          </>
                        )}
                        <TableCell component="th" scope="col" sx={{ minWidth: 80, textAlign: 'center' }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {models.map((model) => (
                        <React.Fragment key={model.id}>
                          <TableRow>
                            <TableCell sx={{ width: '15%' }}>
                              <TextField
                                fullWidth
                                size="small"
                                required
                                label="Model"
                                value={model.model}
                                onChange={(e) => handleModelChange(model.id, 'model', e.target.value)}
                                disabled={model.hasWorkOrder}
                                inputProps={{ 
                                  'aria-label': `Model for row ${model.id}`, 
                                  'aria-required': 'true',
                                  maxLength: 50
                                }}
                                sx={{ minWidth: 100 }}
                                error={(!validateFieldCharacterLimit('modelCode', model.model).isValid || hasModelDuplicateError(model.id, 'model')) && hasAttemptedSubmit}
                                helperText={
                                  (hasModelDuplicateError(model.id, 'model') && hasAttemptedSubmit) ? getModelDuplicateErrorMessage(model.id, 'model') :
                                  getCharacterCountHelperText('modelCode', model.model)
                                }
                                FormHelperTextProps={{
                                  sx: { 
                                    color: (hasModelDuplicateError(model.id, 'model') && hasAttemptedSubmit) ? 'error.main' : getCharacterCountColor('modelCode', model.model)
                                  }
                                }}
                                InputProps={{
                                  endAdornment: !model.hasWorkOrder && (
                                    <IconButton
                                      size="small"
                                      onClick={() => handleModelSearch(model.id, 'model')}
                                      aria-label={`Search models for row ${model.id}`}
                                      title="Search Models"
                                      edge="end"
                                    >
                                      <Search aria-hidden="true" />
                                    </IconButton>
                                  )
                                }}
                              />
                            </TableCell>
                            {(formData.typeOfWork === 'CFG' || formData.typeOfWork === 'DFG') && (
                              <TableCell sx={{ width: '15%' }}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="To"
                                  value={model.to}
                                  onChange={(e) => handleModelChange(model.id, 'to', e.target.value)}
                                  disabled={model.hasWorkOrder}
                                  inputProps={{ 
                                    'aria-label': `To model for row ${model.id}`,
                                    maxLength: 50
                                  }}
                                  sx={{ minWidth: 100 }}
                                  error={(!validateFieldCharacterLimit('modelCode', model.to).isValid || hasModelDuplicateError(model.id, 'to')) && hasAttemptedSubmit}
                                  helperText={
                                    (hasModelDuplicateError(model.id, 'to') && hasAttemptedSubmit) ? getModelDuplicateErrorMessage(model.id, 'to') :
                                    getCharacterCountHelperText('modelCode', model.to)
                                  }
                                  FormHelperTextProps={{
                                    sx: { 
                                      color: (hasModelDuplicateError(model.id, 'to') && hasAttemptedSubmit) ? 'error.main' : getCharacterCountColor('modelCode', model.to)
                                    }
                                  }}
                                  InputProps={{
                                    endAdornment: !model.hasWorkOrder && (
                                      <IconButton
                                        size="small"
                                        onClick={() => handleModelSearch(model.id, 'to')}
                                        aria-label={`Search models for To field in row ${model.id}`}
                                        title="Search Models"
                                        edge="end"
                                      >
                                        <Search aria-hidden="true" />
                                      </IconButton>
                                    )
                                  }}
                                />
                              </TableCell>
                            )}
                            <TableCell sx={{ width: '10%' }}>
                              <TextField
                                fullWidth
                                size="small"
                                required
                                label="Quantity"
                                type="number"
                                value={model.quantity}
                                onChange={(e) => handleModelChange(model.id, 'quantity', e.target.value)}
                                disabled={model.hasWorkOrder}
                                inputProps={{ 'aria-label': `Quantity for row ${model.id}`, 'aria-required': 'true', min: 1 }}
                                sx={{ minWidth: 80 }}
                              />
                            </TableCell>
                            <TableCell sx={{ width: '12%', minWidth: 100 }}>
                              <TextField
                                fullWidth
                                size="small"
                                required
                                label="Date Required"
                                type="date"
                                value={model.dateRequired}
                                onChange={(e) => handleModelChange(model.id, 'dateRequired', e.target.value)}
                                disabled={model.hasWorkOrder}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ 'aria-label': `Date required for row ${model.id}`, 'aria-required': 'true' }}
                                sx={{ minWidth: 100 }}
                              />
                            </TableCell>
                            {projectCode && (
                              <>
                                <TableCell sx={{ width: '10%', minWidth: 100 }}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Date of Job"
                                    type="date"
                                    value={model.dateOfJobLine || ''}
                                    disabled
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ 'aria-label': `Date of job for row ${model.id}` }}
                                    sx={{ minWidth: 100 }}
                                  />
                                </TableCell>
                                <TableCell sx={{ width: '8%', minWidth: 80 }}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Line"
                                    value={model.lineNum || ''}
                                    disabled
                                    inputProps={{ 'aria-label': `Line number for row ${model.id}` }}
                                    sx={{ minWidth: 80 }}
                                  />
                                </TableCell>
                                <TableCell sx={{ width: '12%', minWidth: 120 }}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="PIC"
                                    value={model.personInCharge || ''}
                                    disabled
                                    inputProps={{ 'aria-label': `Person in charge for row ${model.id}` }}
                                    sx={{ minWidth: 120 }}
                                  />
                                </TableCell>
                              </>
                            )}
                            
                            <TableCell sx={{ width: '8%' }}>
                              <Tooltip title={
                                models.length === 1 ? 'Cannot delete the last row' :
                                model.hasWorkOrder ? 'Cannot delete row with work order' :
                                `Delete row ${model.id}`
                              }>
                                <span>
                                  <IconButton 
                                    onClick={() => deleteModelRow(model.id)}
                                    disabled={models.length === 1 || model.hasWorkOrder}
                                    color="error"
                                    aria-label={`Delete model row ${model.id}`}
                                    title={models.length === 1 ? 'Cannot delete the last row' : model.hasWorkOrder ? 'Cannot delete row with work order' : `Delete row ${model.id}`}
                                  >
                                    <Delete aria-hidden="true" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </SectionCard>

            {/* Enhanced Important Notice */}
            <Alert 
              severity="info" 
              sx={{ 
                mb: 4, 
                borderRadius: 3,
                border: '1px solid #e3f2fd',
                backgroundColor: '#f8fbff',
                '& .MuiAlert-message': { fontSize: '1rem' },
                '& .MuiAlert-icon': { fontSize: 28 }
              }}
              icon={<Info aria-hidden="true" />}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'info.main' }}>
                <span aria-hidden="true">📋 </span>Important Notice
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                Once you submit this form, you will need to contact an administrator to make further changes to your request. 
                Please review all information carefully before submitting.
              </Typography>
            </Alert>

            {/* Enhanced Action Buttons */}
            <Box sx={{ 
              display: 'flex', 
              gap: 3, 
              justifyContent: 'space-between', 
              alignItems: 'center',
              pt: 3,
              borderTop: '2px solid #f0f0f0',
              mt: 4
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Form completion status
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <ModernButton 
                  variant="outlined" 
                  onClick={() => navigate('/customer-request')}
                  size="large"
                  sx={{ minWidth: 140 }}
                >
                  Back
                </ModernButton>
                <ModernButton 
                  variant="contained" 
                  type="submit"
                  disabled={loading || duplicateModelErrors.length > 0}
                  size="large"
                  sx={{ 
                    minWidth: 160,
                    background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)'
                    },
                    '&:disabled': {
                      background: '#e0e0e0'
                    }
                  }}
                >
                  {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} color="inherit" aria-hidden="true" />
                      <Typography>Processing...</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle sx={{ fontSize: 20 }} aria-hidden="true" />
                      {projectCode ? 'Update Request' : 'Submit Request'}
                    </Box>
                  )}
                </ModernButton>
              </Box>
            </Box>
          </form>
        </CardContent>
      </ModernCard>
      
      <ModelSearchDialog
        open={modelSearchOpen}
        onClose={handleModelSearchClose}
        onSelect={handleModelSelect}
        title={`Select ${searchFieldType === 'model' ? 'Model' : 'To Model'}`}
      />
      

    </Container>
  );
};

export default CustomerRequestForm;